<script>
  import { copyToClipboard } from '@/helpers/utils';
  import CloseIcon from '@/assets/images/svg/icons/cancel-circle.svg';
  import CopyIcon from '@/assets/images/svg/icons/share-copy.svg';

  export default {
    components: {
      CloseIcon,
      CopyIcon,
    },

    props: {
      referralLink: {
        type: String,
        default: '',
      },
      value: {
        type: Boolean,
        required: true,
      },
    },

    data() {
      return {
        sharing: {
          url: this.referralLink,
          title: "You've been invited to MaxQuest!",
          description:
            'Join me at MaxQuest, Play Games of Skill to Win Real Cash Prizes. Play exciting games and win real crypto prizes for FREE. Use my link to get started with your free bonus coins.',
          hashtags: 'MaxQuest, PlayerVsPlayer, CryptoGaming',
        },
        networks: [
          {
            network: 'facebook',
            name: 'Facebook',
            icon: () => import('@/assets/images/svg/icons/share-facebook.svg'),
          },
          {
            network: 'twitter',
            name: 'X',
            icon: () => import('@/assets/images/svg/icons/share-twitter.svg'),
          },
          {
            network: 'whatsapp',
            name: 'Whatsapp',
            icon: () => import('@/assets/images/svg/icons/share-whatsapp.svg'),
          },
          {
            network: 'gmail',
            name: 'GMail',
            icon: () => import('@/assets/images/svg/icons/share-email.svg'),
          },
        ],
        popoverText: '',
        timerId: null,
      };
    },

    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },

      popupMaxWidth() {
        if (this.$vuetify.breakpoint.width >= 948) return 900;
        if (this.$vuetify.breakpoint.width >= 500) return 470;
        return 360;
      },
    },

    watch: {
      '$store.state.sidebar': function (newValue) {
        if (this.$vuetify.breakpoint.xs) {
          if (newValue === true) {
            this.show = false;
          }
        }
      },
    },

    methods: {
      closeModal() {
        this.show = false;
      },

      async copyLink() {
        if (this.popoverText) return;

        if (!this.referralLink) {
          this.popoverText = 'Generate referral link first';
          this.showPopover();
          return;
        }

        try {
          await copyToClipboard(this.referralLink);
          this.popoverText = 'Link copied to clipboard';
          this.showPopover();
        } catch (error) {
          console.error(`ShareLink copy error: ${error}`);
        }
      },

      showPopover() {
        const { popover, copyButton } = this.$refs;
        if (!popover || !copyButton) return;

        const rect = copyButton.getBoundingClientRect();
        popover.style.top = `${rect.y - rect.height * 2}px`;
        popover.style.left = `${rect.x}px`;

        popover.showPopover();

        clearTimeout(this.timerId);
        this.timerId = setTimeout(() => {
          popover?.hidePopover();
          this.popoverText = '';
        }, 1000);
      },
    },
  };
</script>

<template>
  <v-dialog
    v-model="show"
    :max-width="popupMaxWidth"
    content-class="share-link-dialog fixed-right"
    no-click-animation
    transition="dialog-top-transition"
  >
    <CloseIcon class="close-button" @click="closeModal" />

    <h2 class="dialog-title">Share My Referral Link</h2>
    <p class="dialog-lead">Choose how you want to share the link</p>

    <section class="share-networks">
      <div class="share-network-copy" @click="copyLink">
        <div ref="popover" class="popover-tooltip" popover role="tooltip">
          {{ popoverText }}
        </div>

        <div ref="copyButton" class="social-network-wrapper">
          <CopyIcon />
          <span>Copy Link</span>
        </div>
      </div>
      <ShareNetwork
        v-for="network in networks"
        :key="network.network"
        :description="sharing.description"
        :hashtags="sharing.hashtags"
        :network="network.network"
        :quote="sharing.quote"
        :title="sharing.title"
        :twitterUser="sharing.twitterUser"
        :url="referralLink"
        tag="div"
      >
        <div class="social-network-wrapper">
          <component :is="network.icon" />
        </div>
      </ShareNetwork>
    </section>
  </v-dialog>
</template>

<style lang="scss">
  @import 'src/scss/mixins';

  .share-link-dialog {
    position: absolute;
    z-index: 9999;
    padding: 60px 30px;
    border-radius: 20px;
    margin: 0 24px;
    background: var(--Colors-White, #fff);
    filter: drop-shadow(1px 1px 5px #000000);
    text-align: center;

    @media screen and (min-width: 948px) {
      padding: 60px;
    }

    .close-button {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 24px;
      height: 24px;
      cursor: pointer;

      &:hover {
        filter: brightness(105%);
      }

      &:active {
        filter: brightness(97%);
      }
    }

    .dialog-title {
      color: #1e1e1e;
      font-size: 28px;
      font-weight: 600;
      line-height: 26px;
      margin-bottom: 0;

      @media screen and (min-width: 500px) {
        font-size: 32px;
      }
    }

    .dialog-lead {
      margin-top: 24px;
      margin-bottom: 0;
      color: #1e1e1e;
      font-size: 18px;
      font-weight: 400;
      line-height: 26px;
    }

    .share-networks {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      margin-top: 40px;
    }

    .social-network-wrapper {
      width: 58px;
      padding: 10px 0;
      cursor: pointer;
      display: flex;
      justify-content: center;
      border-radius: var(--Spacing-xs, 4px);

      @media screen and (min-width: 500px) {
        width: 78px;
        padding: 12px 0;
      }

      @media screen and (min-width: 948px) {
        width: 133px;
      }

      &:hover {
        filter: brightness(105%);
      }

      &:active {
        filter: brightness(97%);
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .share-network {
      &-copy .social-network-wrapper {
        background-color: #350f53;

        span {
          display: none;

          @media screen and (min-width: 948px) {
            display: inline-block;
            margin-left: 10px;
            color: var(--Colors-White, #fff);
            font-size: 14px;
            font-weight: 500;
            line-height: 26px;
          }
        }
      }

      &-facebook .social-network-wrapper {
        background-color: #0865ff;
      }

      &-twitter .social-network-wrapper {
        box-shadow: 0 0 0 1px inset #000;
      }

      &-whatsapp .social-network-wrapper {
        background-color: #27d045;
      }

      &-gmail .social-network-wrapper {
        background-color: #327dc1;
      }
    }

    .popover-tooltip {
      padding: 8px 12px;
      border-radius: 12px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      background: #160526;
      color: #96d7ff;
      font-size: 14px;
      line-height: 1;
    }
  }
</style>
